import { useState, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { Columns2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SecondarySidebarLayoutProps {
  title: string;
  /** The sidebar's links, under the title. */
  children: ReactNode;
}

/** A collapsible second sidebar beside the routed page (kim-ai-frontend's layout). */
export function SecondarySidebarLayout({ title, children }: SecondarySidebarLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    // The full viewport height, so the page scrolls and the sidebar stays put.
    <div className='flex h-svh overflow-hidden'>
      <div className={cn('overflow-hidden shrink-0 transition-[width] duration-200 ease-linear', collapsed ? 'w-0' : 'w-[200px]')}>
        <div className='w-[200px] h-full flex flex-col bg-gray-100/80 backdrop-blur-[6px] border-r border-gray-500 p-3 gap-8'>
          <div className='flex items-end justify-between gap-2 pt-0.5 shrink-0'>
            <span className='min-w-0 truncate text-base font-semibold text-black-400' title={title}>{title}</span>
            <button
              onClick={() => setCollapsed(true)}
              className='p-1.5 rounded-full hover:bg-neutral-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer'
              aria-label='Collapse sidebar'
            >
              <Columns2 className='size-4 text-gray-800' />
            </button>
          </div>
          {children}
        </div>
      </div>

      <div className={cn('flex-1 relative overflow-auto transition-[padding] duration-200 ease-linear', collapsed && 'pl-8')}>
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className='absolute top-4 left-2 z-10 p-1.5 rounded-full hover:bg-neutral-50 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer'
            aria-label='Expand sidebar'
          >
            <Columns2 className='size-4 text-gray-800' />
          </button>
        )}
        <Outlet />
      </div>
    </div>
  );
}
